import { useMessagesRepository } from '@Hooks/useRepository';
import type { ApiScaleInfo, ApiWeighing } from '@Model';
import { ScalesRepository } from '@Repositories/Messages';
import { unwrapOr, useToasts } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { type IScalesContext, ScalesContext } from './ScalesContext';

export function ScalesProvider(props: PropsWithChildren) {
    const scalesApi = useMessagesRepository(ScalesRepository);
    const { pushAllToasts } = useToasts();
    const [selectedScale, setSelectedScale] = useState<ApiScaleInfo>();

    const { data: scales } = useQuery({
        queryFn: async () => {
            const result = await scalesApi.getScales();
            return unwrapOr(result, []);
        },
        queryKey: ['scales', 'list'],
        initialData: [],
    });

    const getWeight = useCallback(
        async (scale: ApiScaleInfo): Promise<ApiWeighing | undefined> => {
            const scaleId = scale.id;
            const reloadedScale = scales.find((s) => s.id === scaleId);

            // The requested scale does not exist!
            if (!reloadedScale) {
                return undefined;
            } else if (reloadedScale.is_manual) {
                return undefined;
            }

            const scaleResult = await scalesApi.getWeight(scaleId);

            pushAllToasts(scaleResult.messages);

            return scaleResult.data;
        },
        [scales, scalesApi, pushAllToasts],
    );

    useEffect(() => {
        if (selectedScale === undefined && scales?.length > 0) {
            setSelectedScale(scales[0]);
        }
    }, [scales, selectedScale]);

    const canUseAutomaticWeighing = selectedScale !== undefined && !selectedScale.is_manual;

    const scalesContext: IScalesContext = {
        availableScales: scales,
        canUseAutomaticWeighing,
        selectedScale,

        selectScale: setSelectedScale,
        getWeight,
    };

    return <ScalesContext.Provider value={scalesContext}>{props.children}</ScalesContext.Provider>;
}
